import React, { Component } from 'react';
import debounce from 'lodash.debounce';
import { detailedDiff } from 'deep-object-diff';
import memoizeOne from 'memoize-one';
import Cookies from 'js-cookie';
import { RemoteCapsule, Lifecycle, CapsuleType } from '@bit/amit.capsule-dev.remote-capsule';

import config from '../../config';
import { router } from '../../state/capsule-routes';
import { CapsuleDef } from '../../capsule';
import { CAPSULE_FRAME_ID } from '../../constants';
import { MemfsJson } from '../../state/files-store';
import { ConnectionIndicator } from '../connection-status';
import { SmallBrickLoader } from '../brick-loader';
import { Mask } from '../mask';
import { BigLoader } from './big-loader/big-loader';
import { HmrSocket } from './hmr-socket';
import { LoaderCtrl } from './loader-ctrl';
import { IPromisesAPI } from 'memfs/lib/promises';

import styles from './capsule-ctrl.module.css';
import { MethodProxy } from '../../logic/method-proxy';
import { FsSensor } from '../../logic/fs-sensor';

/**
 * we could split this class into these three:
 * 1. <CapsuleView capsule={capsule | undefined} definition={capsuleDef?}/>
 * 		* handle url from Lifecycle.Connected. (could actually just use remoteCapsule.Url ?)
 * 		* reflect states to the user (could just use remoteCapsule.connectionState ?)
 * 		* should somehow know when to (re)connect to HMR socket
 * 2. setuper?
 * 3. new CapsuleWriter(capsule)
 * 		* writes files to capsule.
 * 		* debounce and write-lock
 * 		(might not be necessary if we drop bit.dev examples)
 */

type CapsuleProps = {
	isVisible?: boolean;
	type?: CapsuleType;
	files?: MemfsJson; //TODO - use fs directly, outside of this component
	codeId?: string;
	bitId?: string;
	onFsChange?: (fs: IPromisesAPI | undefined) => any;
	onCapsuleChange?: (capsule?: RemoteCapsule) => any;
};

type CapsuleState = {
	isWorking: boolean;
	isCapsuleWorking: boolean;
	isSettingUp: boolean;
	url?: string;
	iframeKey: number;
	status?: Lifecycle;

	capsuleDef?: CapsuleDef;
};

const _deadStatuses = new Set([undefined, Lifecycle.Idle, Lifecycle.Disconnected]);

const SOCKET_OPTIONS = { uri: config.orchestratorUrl };
const COOKIE = Cookies.get(config.cookieName);

export class CapsuleCtrl extends Component<CapsuleProps, CapsuleState> {
	state: CapsuleState = {
		isWorking: false,
		isCapsuleWorking: false,
		isSettingUp: true,
		status: undefined,
		iframeKey: 0,
	};

	static defaultProps = {
		isVisible: true,
	};

	private capsuleWriteLock: boolean = false;
	private capsule?: RemoteCapsule;

	private _loaderCtrl: LoaderCtrl;
	private resolveIframePromise?: () => void;
	private handleIframeLoaded = (e?: React.SyntheticEvent<HTMLIFrameElement>) => {
		console.debug('...iframe loaded');

		this.resolveIframePromise && this.resolveIframePromise();
	};

	constructor(props: CapsuleProps) {
		super(props);

		this._loaderCtrl = new LoaderCtrl();
		this._loaderCtrl.onSetupChange = isSettingUp => this.setState({ isSettingUp });
		this._loaderCtrl.onWorkingChange = isWorking => this.setState({ isWorking });
	}

	componentDidMount() {
		const { type, bitId } = this.props;
		if (type) {
			this.getCapsuleInstance(type, bitId)
				.then(() => this.startCapsule())
				.then(() => this.syncFilesToCapsule());
		}
	}

	componentWillUnmount() {
		this.destroyCapsule();
	}

	componentDidUpdate(prevProps: CapsuleProps) {
		const nextProps = this.props;

		const capsuleTypeMutated =
			prevProps.type !== nextProps.type || prevProps.bitId !== nextProps.bitId;

		if (!nextProps.type) return;

		//example changed, start loader:
		if (prevProps.codeId != nextProps.codeId) {
			this._loaderCtrl.handleExampleChanges();
		}

		if (capsuleTypeMutated) {
			this.destroyCapsule();
			//do not await, continue to write files
			this.getCapsuleInstance(nextProps.type, nextProps.bitId);
		}

		if (prevProps.files !== nextProps.files && nextProps.files) {
			this.syncFilesToCapsule();
		}
	}

	//expose status by ref
	get status() {
		return this.state.status;
	}

	private _getCapsuleInstance = (capsuleType: CapsuleType, bitId: string | undefined) => {
		const capsuleDef = router.get(capsuleType);
		if (!capsuleDef) throw new Error(`no such capsule type as "${capsuleType}"`);

		this.setState({ capsuleDef: capsuleDef });

		const { factory } = capsuleDef;

		const options = { socketOptions: SOCKET_OPTIONS, cookie: COOKIE, capsuleType, bitId };
		return factory(options).then(capsule => {
			this.capsule = capsule;
			return capsule;
		});
	};

	private getCapsuleInstance = memoizeOne(this._getCapsuleInstance);

	// ignite capsule engine
	private startCapsule = () => {
		const { onFsChange, onCapsuleChange } = this.props;
		const { capsuleDef } = this.state;
		const { capsule } = this;
		if (!capsule) throw new Error('capsule should be initiated before starting connection');

		this.prevWritten = {};

		const promise = new Promise<string | undefined>((resolve, reject) => {
			const handleCapsuleConnected = ({ url }: { url?: string } = {}) => {
				this.resolveIframePromise = resolve;
				// should this be a private property?
				const monitor = new FsSensor({
					observers: this._loaderCtrl.handleInstallFiles,
				});

				const fs = capsule.fs;
				const wrappedFs = MethodProxy(fs, monitor.middleware);
				onFsChange && onFsChange(wrappedFs);
				onCapsuleChange && onCapsuleChange(this.capsule);

				if (!url) return resolve();

				this.setState({ url: url });
				if (capsuleDef && capsuleDef.hmrEnabled) {
					this.connectToHmr(url);
				}
			};

			//TODO - move this responsibility to remoteCapsule
			capsule.on(Lifecycle.Connected, handleCapsuleConnected);

			Object.values(Lifecycle)
				.map(x => x as Lifecycle)
				.forEach(x => capsule.on(x, () => this.handleConnectionChanged(x)));

			capsule.start();
		});

		return promise;
	};

	// Hot Module Reload listens to Webpack, and activates loader when compiling
	hmrSocket?: HmrSocket;
	private connectToHmr = (url: string) => {
		this.hmrSocket = new HmrSocket(url);
		let resolvePacking = () => {};

		this.hmrSocket.onStartPacking = () => {
			this.setState({ isCapsuleWorking: true });

			resolvePacking();
			this._loaderCtrl.handleWebpackRepacking(new Promise(res => (resolvePacking = res)));
		};
		this.hmrSocket.onDidPack = () => {
			this.setState({ isCapsuleWorking: false });
			resolvePacking();
		};
		this.hmrSocket.onProblem = () => {
			this.setState({ isCapsuleWorking: false });
			resolvePacking();
		};
	};

	private disengageCapsule = () => {
		const { onFsChange, onCapsuleChange } = this.props;
		this.syncFilesToCapsule.cancel();
		this.capsuleWriteLock = false;
		this.prevWritten = {};

		this.capsule = undefined;

		if (this.hmrSocket) {
			this.hmrSocket.destroy();
			this.hmrSocket = undefined;
		}
		this.setState({
			url: undefined,
			status: undefined,
		});
		this._loaderCtrl.reset(true);
		onFsChange && onFsChange(undefined);
		onCapsuleChange && onCapsuleChange(undefined);
	};

	private async destroyCapsule() {
		const { capsule } = this;

		this.disengageCapsule();

		if (capsule) {
			await capsule.destroy();
		}
	}

	handleConnectionChanged = (nextState: Lifecycle) => {
		if (this.state.status === nextState) return;

		this.setState({ status: nextState });
	};

	private syncFilesToCapsule = debounce(() => {
		const { capsule } = this;
		const { files } = this.props;
		const { status } = this.state;

		//nothing to write:
		if (!files) {
			this.capsuleWriteLock = false;
			return;
		}

		if (!capsule || this.capsuleWriteLock) {
			this.syncFilesToCapsule(); //recursion!! try again after debounce
			return;
		}

		this.capsuleWriteLock = true;

		const promise = Promise.resolve()
			.then(() => {
				if (_deadStatuses.has(status)) {
					return this.startCapsule();
				}
				return undefined;
			})
			.then(() => {
				return this.writeFilesToCapsule(capsule, files);
			})
			.catch()
			.then(() => {
				this.capsuleWriteLock = false;
			});

		this._loaderCtrl.handleInstallFiles(promise);
	}, 600);

	private prevWritten: MemfsJson = {};
	private async writeFilesToCapsule(currentCapsule: RemoteCapsule, files: MemfsJson) {
		const { added, deleted, updated } = detailedDiff(this.prevWritten, files) as {
			added: MemfsJson;
			deleted: MemfsJson;
			updated: MemfsJson;
		};

		try {
			await currentCapsule.updateFs({ ...added, ...updated });

			await Promise.all(
				Object.keys(deleted).map(filepath => {
					return currentCapsule.fs.unlink(filepath);
				})
			);

			this.prevWritten = files;
		} catch (e) {
			console.error('failed writing files to capsule', e);
			this.prevWritten = {};
			return;
		}
	}

	reload = () => {
		console.debug('reloading... ');
		const connectionState = this.status;

		if (_deadStatuses.has(connectionState)) {
			this.handleIframeLoaded();
			return;
		}

		this.setState({ iframeKey: this.state.iframeKey + 1 });
	};

	render() {
		const { isVisible = true } = this.props;
		const {
			url,
			iframeKey,
			status,
			isWorking,
			isCapsuleWorking,
			isSettingUp,

			capsuleDef,
		} = this.state;

		const capsuleLogo = capsuleDef && capsuleDef.logoUrl;

		const bigLoader = (
			<BigLoader
				topFace={capsuleLogo && <img className={styles.logo} src={capsuleLogo} />}
			/>
		);

		return (
			<div className={`${styles.capsuleContainer} ${isVisible ? '' : styles.hidden}`}>
				<Mask mask={bigLoader} maskVisible={isSettingUp}>
					<SmallBrickLoader
						className={styles.smallLoader}
						visible={(isWorking || isCapsuleWorking) && !isSettingUp}
					/>

					<iframe
						className={styles.iframe}
						key={iframeKey}
						src={url}
						id={CAPSULE_FRAME_ID}
						name={CAPSULE_FRAME_ID}
						onLoad={this.handleIframeLoaded}
						height="100%"
						width="100%"
						sandbox="allow-forms allow-scripts allow-same-origin"
					/>
				</Mask>

				<ConnectionIndicator status={status} />
			</div>
		);
	}
}
